import React, { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError, API_BASE } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";
import {
  ScanLine, Search, Loader2, ArrowLeft, Camera, X, Check, PackageCheck,
  Store, ChevronRight, Plus, Minus, RotateCcw, ImageOff, LogOut, BadgeCheck, Package,
  Droplets, Paintbrush, Waves, Scissors, MoveHorizontal, Wind, PackageX, Tag, Shirt, Sparkles, Keyboard,
} from "lucide-react";
import CameraCapture from "@/components/CameraCapture";
import BarcodeScanner from "@/components/BarcodeScanner";
import { CONDITION_TAGS, RETURN_REASONS } from "@/lib/returns";
import { motion, AnimatePresence } from "framer-motion";

const LOGO = "https://customer-assets-7cd3h4nn.emergentagent.net/job_e93c448b-a938-4bcf-95ba-463bd62fed43/artifacts/2d6ua9tk_Logo_Wide_White%20%283%29.png";

const TAG_ICONS = {
  Droplets, Paintbrush, Waves, Scissors, MoveHorizontal, Wind, PackageX, Tag, Shirt, Sparkles,
};

const norm = (s) => String(s || "").trim().toLowerCase();

export default function ReceivingStation() {
  const navigate = useNavigate();
  const { isStaff } = useAuth();
  const [accounts, setAccounts] = useState([]);
  const [account, setAccount] = useState(null);
  const [kbd, setKbd] = useState(() => localStorage.getItem("bh-rs-kbd") === "1"); // per-device soft-keyboard preference
  const [step, setStep] = useState("store"); // store | scan | inspect | done
  const [query, setQuery] = useState("");
  const [mode, setMode] = useState("order"); // order | tracking
  const [looking, setLooking] = useState(false);
  const [order, setOrder] = useState(null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("");
  const [returnTracking, setReturnTracking] = useState("");
  const [cameraItem, setCameraItem] = useState(null); // index of item being photographed
  const [zoomImg, setZoomImg] = useState(null); // enlarged ShipStation reference photo
  const [scanOrder, setScanOrder] = useState(false); // camera scanner for order/tracking
  const [scanItems, setScanItems] = useState(false); // continuous camera scanner to verify items
  const [pendingUploads, setPendingUploads] = useState(0);
  const [saving, setSaving] = useState(false);
  const uploading = pendingUploads > 0;
  const [lastResult, setLastResult] = useState(null);
  const scanRef = useRef(null);
  const autoTimerRef = useRef(null);

  useEffect(() => {
    api.get("/staff/accounts").then(({ data }) => setAccounts(data.accounts || [])).catch(() => {});
  }, []);

  useEffect(() => {
    if (step === "scan" && scanRef.current) scanRef.current.focus();
  }, [step]);

  const resetForNext = () => {
    setQuery(""); setOrder(null); setItems([]); setReason("");
    setReturnTracking(""); setLastResult(null); setStep("scan");
  };

  const doLookup = async (value) => {
    if (looking) return;
    const q = (value != null ? value : query).trim();
    if (!q) { toast.error("Scan or type an order/tracking number"); return; }
    setQuery(q);
    setLooking(true);
    try {
      const params = { account_id: account.id };
      if (mode === "tracking") params.tracking = q; else params.order_number = q;
      const { data } = await api.get("/returns/lookup", { params });
      if (!data.found) { toast.error("No matching order found for this store"); return; }
      setOrder(data);
      setReturnTracking(mode === "tracking" ? q : "");
      setItems((data.items || []).map((it) => ({
        sku: it.sku, name: it.name, barcode: it.barcode || "", image_url: it.image_url || "",
        qty_shipped: it.quantity, qty_returned: 0, condition_tags: [], note: "",
        photos: [], verified: false,
      })));
      setStep("inspect");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLooking(false);
    }
  };

  const setItem = (idx, patch) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, ...patch } : it)));
  const bumpQty = (idx, delta) => setItems((arr) => arr.map((it, i) => {
    if (i !== idx) return it;
    const next = Math.max(0, Math.min(it.qty_shipped || 99, (it.qty_returned || 0) + delta));
    return { ...it, qty_returned: next };
  }));
  const setQty = (idx, val) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, qty_returned: Math.max(0, Math.min(it.qty_shipped || 99, val)) } : it)));
  const toggleTag = (idx, tag) => setItems((arr) => arr.map((it, i) => {
    if (i !== idx) return it;
    const has = (it.condition_tags || []).includes(tag);
    return { ...it, condition_tags: has ? it.condition_tags.filter((t) => t !== tag) : [...it.condition_tags, tag] };
  }));

  // Scan an item barcode to verify + count it in.
  const onItemScan = (code) => {
    const c = norm(code);
    let matchedIdx = -1;
    setItems((arr) => arr.map((it, i) => {
      if (matchedIdx === -1 && (norm(it.barcode) === c || norm(it.sku) === c)) {
        matchedIdx = i;
        const next = Math.min(it.qty_shipped || 99, (it.qty_returned || 0) + 1);
        return { ...it, qty_returned: next, verified: true };
      }
      return it;
    }));
    setTimeout(() => {
      if (matchedIdx === -1) toast.error(`Barcode ${code} isn't on this order`);
      else toast.success("Item verified +1");
    }, 0);
  };

  const uploadPhoto = async (idx, blobOrFile, filename) => {
    setPendingUploads((n) => n + 1);
    try {
      const fd = new FormData();
      fd.append("file", blobOrFile, filename);
      fd.append("account_id", account.id);
      const { data } = await api.post("/claims/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, photos: [...it.photos, data] } : it)));
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setPendingUploads((n) => Math.max(0, n - 1));
    }
  };

  const removePhoto = (idx, id) => setItems((arr) => arr.map((it, i) => (i === idx ? { ...it, photos: it.photos.filter((x) => x.id !== id) } : it)));

  const complete = async () => {
    const totalUnits = items.reduce((s, it) => s + (it.qty_returned || 0), 0);
    if (totalUnits === 0) { toast.error("Set a return quantity on at least one item"); return; }
    const missingPhoto = items.filter((it) => (it.qty_returned || 0) > 0 && it.photos.length === 0);
    if (missingPhoto.length > 0) {
      toast.error(`Take at least one photo of: ${missingPhoto.map((i) => i.name || i.sku || "item").join(", ")}`);
      return;
    }
    setSaving(true);
    try {
      const payload = {
        account_id: account.id,
        order_number: order.order_number,
        return_tracking: returnTracking || null,
        order_source: "manual",
        reason,
        customer_name: order.customer_name || null,
        items: items.filter((it) => (it.qty_returned || 0) > 0).map((it) => ({
          sku: it.sku, name: it.name, barcode: it.barcode || "",
          qty_shipped: it.qty_shipped, qty_returned: it.qty_returned,
          condition_tags: it.condition_tags, note: it.note || "",
          photos: it.photos.map((p) => ({ id: p.id, storage_path: p.storage_path, original_filename: p.original_filename, content_type: p.content_type })),
        })),
        status: "completed",
      };
      await api.post("/returns", payload);
      const photoCount = items.reduce((s, it) => s + it.photos.length, 0);
      setLastResult({ order_number: order.order_number, units: totalUnits, photos: photoCount });
      setStep("done");
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  if (!isStaff) return null;

  const totalUnits = items.reduce((s, it) => s + (it.qty_returned || 0), 0);
  const returnedItems = items.filter((i) => i.qty_returned > 0).length;
  const missingPhoto = items.filter((it) => (it.qty_returned || 0) > 0 && it.photos.length === 0);

  return (
    <div className="fixed inset-0 bg-slate-900 text-white flex flex-col overflow-hidden" data-testid="receiving-station">
      <header className="flex items-center justify-between px-6 py-4 bg-slate-950/60 border-b border-white/10 shrink-0">
        <div className="flex items-center gap-4">
          <img src={LOGO} alt="BoxHero" className="h-8 w-auto" />
          <div className="h-6 w-px bg-white/20" />
          <div className="flex items-center gap-2 text-lg font-bold" style={{ fontFamily: "Manrope" }}>
            <PackageCheck className="w-6 h-6 text-orange-400" /> Returns Receiving
          </div>
          {account && (
            <button onClick={() => { setAccount(null); setStep("store"); }} data-testid="rs-change-store" className="ml-2 inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 px-3 py-1.5 rounded-lg text-sm font-medium">
              <Store className="w-4 h-4 text-orange-400" /> {account.company_name} <RotateCcw className="w-3.5 h-3.5 opacity-60" />
            </button>
          )}
        </div>
        <button onClick={() => navigate("/admin")} data-testid="rs-exit" className="inline-flex items-center gap-2 text-white/70 hover:text-white text-sm font-medium px-3 py-2 rounded-lg hover:bg-white/10">
          <X className="w-5 h-5" /> Exit station
        </button>
      </header>

      <main className="flex-1 overflow-y-auto">
        {/* STEP 1 — pick store */}
        {step === "store" && (
          <div className="max-w-3xl mx-auto px-6 py-10" data-testid="rs-step-store">
            <h1 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "Manrope" }}>Which store are you receiving for?</h1>
            <p className="text-white/60 mb-8">Pick the brand whose package you&apos;re processing.</p>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {accounts.map((a) => (
                <button key={a.id} data-testid={`rs-store-${a.id}`} onClick={() => { setAccount(a); setStep("scan"); }} disabled={!a.linked}
                  className="flex items-center justify-between text-left bg-white/5 border border-white/10 hover:border-orange-400 hover:bg-white/10 rounded-2xl px-5 py-5 transition-all disabled:opacity-40 disabled:cursor-not-allowed">
                  <div>
                    <p className="text-lg font-bold">{a.company_name}</p>
                    <p className="text-xs text-white/50 font-mono mt-0.5">{a.boxhero_account_id}{!a.linked ? " · not linked to ShipStation" : ""}</p>
                  </div>
                  <ChevronRight className="w-6 h-6 text-orange-400 shrink-0" />
                </button>
              ))}
              {accounts.length === 0 && <p className="text-white/50">No stores available.</p>}
            </div>
          </div>
        )}

        {/* STEP 2 — scan / lookup */}
        {step === "scan" && account && (
          <div className="max-w-2xl mx-auto px-6 py-12 text-center" data-testid="rs-step-scan">
            <div className="w-20 h-20 mx-auto rounded-2xl bg-orange-500/20 flex items-center justify-center mb-6">
              <ScanLine className="w-10 h-10 text-orange-400" />
            </div>
            <h1 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "Manrope" }}>Scan or enter the return</h1>
            <p className="text-white/60 mb-8">Scan the barcode with the camera, or type the order / tracking number.</p>

            <div className="flex gap-2 justify-center mb-4" data-testid="rs-mode-toggle">
              {[{ v: "order", l: "Order number" }, { v: "tracking", l: "Tracking number" }].map((m) => (
                <button key={m.v} onClick={() => setMode(m.v)} data-testid={`rs-mode-${m.v}`}
                  className={`px-5 py-2.5 rounded-xl font-semibold text-sm transition-colors ${mode === m.v ? "bg-orange-500 text-white" : "bg-white/10 text-white/70 hover:bg-white/20"}`}>
                  {m.l}
                </button>
              ))}
            </div>

            <div className="flex items-center gap-3 mb-4">
              <input ref={scanRef} data-testid="rs-scan-input" value={query} inputMode={kbd ? "text" : "none"} onChange={(e) => { const v = e.target.value; setQuery(v); if (!kbd) { if (autoTimerRef.current) clearTimeout(autoTimerRef.current); autoTimerRef.current = setTimeout(() => { if (v.trim()) doLookup(v); }, 140); } }}
                onKeyDown={(e) => { if (e.key === "Enter" || e.keyCode === 13) { e.preventDefault(); if (autoTimerRef.current) clearTimeout(autoTimerRef.current); doLookup(e.currentTarget.value); } }}
                placeholder={mode === "tracking" ? "Scan / paste tracking #" : "e.g. BH-100024"}
                className="flex-1 text-2xl font-mono px-6 py-5 rounded-2xl bg-white text-slate-900 placeholder:text-slate-400 focus:outline-none focus:ring-4 focus:ring-orange-400/40" />
              <button onClick={() => { setKbd((k) => { const n = !k; localStorage.setItem("bh-rs-kbd", n ? "1" : "0"); return n; }); setTimeout(() => scanRef.current?.focus(), 20); }} data-testid="rs-keyboard-toggle" title="Toggle on-screen keyboard" className={`inline-flex items-center px-5 py-5 rounded-2xl ${kbd ? "bg-white text-slate-900" : "bg-white/10 text-white/70 hover:bg-white/20"}`}><Keyboard className="w-6 h-6" /></button>
              <button onClick={() => { if (autoTimerRef.current) clearTimeout(autoTimerRef.current); doLookup(scanRef.current?.value); }} disabled={looking} data-testid="rs-lookup-btn"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 disabled:opacity-60 text-white font-bold text-lg px-8 py-5 rounded-2xl">
                {looking ? <Loader2 className="w-6 h-6 animate-spin" /> : <Search className="w-6 h-6" />} Submit
              </button>
            </div>
            <button onClick={() => setScanOrder(true)} data-testid="rs-open-scanner"
              className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-3 rounded-2xl">
              <Camera className="w-5 h-5 text-orange-400" /> Scan barcode with camera
            </button>
          </div>
        )}

        {/* STEP 3 — inspect */}
        {step === "inspect" && order && (
          <div className="max-w-4xl mx-auto px-6 py-8 pb-40" data-testid="rs-step-inspect">
            <button onClick={() => setStep("scan")} data-testid="rs-back-scan" className="inline-flex items-center gap-2 text-white/60 hover:text-white text-sm mb-4">
              <ArrowLeft className="w-4 h-4" /> Back
            </button>
            <div className="bg-white/5 border border-white/10 rounded-2xl px-6 py-4 mb-6 flex flex-wrap items-center gap-x-8 gap-y-2">
              <div><p className="text-xs uppercase tracking-wider text-white/40">Order</p><p className="text-xl font-bold font-mono">{order.order_number}</p></div>
              {order.customer_name && <div><p className="text-xs uppercase tracking-wider text-white/40">Customer</p><p className="font-semibold">{order.customer_name}</p></div>}
              {order.ship_date && <div><p className="text-xs uppercase tracking-wider text-white/40">Shipped</p><p className="font-semibold">{order.ship_date}</p></div>}
              {order.tracking_number && <div><p className="text-xs uppercase tracking-wider text-white/40">Orig. tracking</p><p className="font-mono text-sm">{order.tracking_number}</p></div>}
            </div>

            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-bold text-white" style={{ fontFamily: "Manrope" }}>Inspect each item</h2>
              <button onClick={() => setScanItems(true)} data-testid="rs-scan-items"
                className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold px-5 py-2.5 rounded-xl">
                <ScanLine className="w-5 h-5" /> Scan items to verify
              </button>
            </div>
            <p className="text-white/50 text-sm mb-4">Every item starts as <span className="font-bold text-white">Not returned</span> — scan an item or tap the number you physically received back. Condition & photos appear once you mark one.</p>

            <div className="space-y-4">
              {items.map((it, idx) => (
                <div key={`item-${idx}`} data-testid={`rs-item-${idx}`} className={`rounded-2xl p-4 sm:p-5 transition-all ${it.qty_returned > 0 ? "bg-white text-slate-900 shadow-lg" : "bg-white/70 text-slate-900"}`}>
                  {/* Header */}
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex items-start gap-3 min-w-0">
                      {it.image_url ? (
                        <button type="button" onClick={() => setZoomImg({ url: it.image_url, name: it.name })} data-testid={`rs-item-img-${idx}`} title="Tap to enlarge"
                          className="w-16 h-16 rounded-xl overflow-hidden border border-slate-200 bg-slate-50 shrink-0 cursor-zoom-in hover:ring-2 hover:ring-orange-400 transition-all">
                          <img src={it.image_url} alt={it.name} referrerPolicy="no-referrer" loading="lazy" className="w-full h-full object-cover"
                            onError={(e) => { e.currentTarget.parentElement.style.display = "none"; }} />
                        </button>
                      ) : (
                        <div className="w-16 h-16 rounded-xl bg-slate-100 border border-slate-200 flex items-center justify-center text-slate-300 shrink-0" data-testid={`rs-item-img-none-${idx}`}><Package className="w-7 h-7" /></div>
                      )}
                      <div className="min-w-0">
                        <p className="font-bold text-lg truncate flex items-center gap-2">
                          {it.name || "Item"}
                          {it.verified && <span className="inline-flex items-center gap-1 text-xs font-semibold text-emerald-700 bg-emerald-100 px-2 py-0.5 rounded-full"><BadgeCheck className="w-3.5 h-3.5" /> Verified</span>}
                        </p>
                        <p className="text-sm text-slate-500 font-mono">{it.sku || "—"}{it.barcode ? ` · ${it.barcode}` : ""} · shipped {it.qty_shipped}</p>
                        <p className="text-[11px] text-slate-400 mt-0.5">Reference photo from ShipStation — match the item before counting.</p>
                      </div>
                    </div>
                    {it.qty_returned > 0
                      ? <span data-testid={`rs-item-state-${idx}`} className="shrink-0 inline-flex items-center gap-1.5 text-sm font-extrabold text-emerald-700 bg-emerald-100 px-3 py-1.5 rounded-full"><Check className="w-4 h-4" /> {it.qty_returned} back</span>
                      : <span data-testid={`rs-item-state-${idx}`} className="shrink-0 text-xs font-semibold text-slate-400 bg-slate-100 px-3 py-1.5 rounded-full">Not returned</span>}
                  </div>

                  {/* Quantity picker */}
                  <div className="mt-4">
                    <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">How many did you get back?</p>
                    {(it.qty_shipped || 1) <= 6 ? (
                      <div className="flex flex-wrap gap-2" data-testid={`rs-qty-pills-${idx}`}>
                        <button onClick={() => setQty(idx, 0)} data-testid={`rs-qty-pill-${idx}-0`} className={`min-w-[3.25rem] px-4 py-2.5 rounded-xl text-sm font-bold border-2 transition-all ${it.qty_returned === 0 ? "bg-slate-900 border-slate-900 text-white" : "bg-slate-50 border-slate-200 text-slate-500 hover:border-slate-400"}`}>None</button>
                        {Array.from({ length: it.qty_shipped || 1 }, (_, n) => n + 1).map((n) => (
                          <button key={n} onClick={() => setQty(idx, n)} data-testid={`rs-qty-pill-${idx}-${n}`} className={`min-w-[3.25rem] px-4 py-2.5 rounded-xl text-lg font-extrabold border-2 leading-none transition-all ${it.qty_returned === n ? "bg-orange-500 border-orange-500 text-white scale-105 shadow" : "bg-slate-50 border-slate-200 text-slate-700 hover:border-orange-300"}`}>{n}{n === it.qty_shipped ? <span className="block text-[9px] font-bold uppercase mt-0.5 opacity-80">all</span> : null}</button>
                        ))}
                      </div>
                    ) : (
                      <div className="flex flex-wrap items-center gap-3" data-testid={`rs-qty-stepper-${idx}`}>
                        <button onClick={() => bumpQty(idx, -1)} data-testid={`rs-qty-minus-${idx}`} className="w-12 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Minus className="w-5 h-5" /></button>
                        <span data-testid={`rs-qty-${idx}`} className={`w-16 text-center text-3xl font-extrabold tabular-nums ${it.qty_returned > 0 ? "text-slate-900" : "text-slate-300"}`}>{it.qty_returned}</span>
                        <button onClick={() => bumpQty(idx, 1)} data-testid={`rs-qty-plus-${idx}`} className="w-12 h-12 rounded-xl bg-slate-100 hover:bg-slate-200 flex items-center justify-center"><Plus className="w-5 h-5" /></button>
                        <button onClick={() => setQty(idx, it.qty_shipped)} data-testid={`rs-qty-all-${idx}`} className="ml-1 px-4 py-3 rounded-xl text-sm font-bold bg-slate-800 hover:bg-slate-900 text-white">All ({it.qty_shipped})</button>
                        {it.qty_returned > 0 && <button onClick={() => setQty(idx, 0)} data-testid={`rs-qty-none-${idx}`} className="px-3 py-3 rounded-xl text-sm font-semibold text-slate-500 hover:text-slate-800">Clear</button>}
                      </div>
                    )}
                  </div>

                  {/* Condition + photos — only once received */}
                  <AnimatePresence initial={false}>
                    {it.qty_returned > 0 && (
                      <motion.div key="details" initial={{ height: 0, opacity: 0 }} animate={{ height: "auto", opacity: 1 }} exit={{ height: 0, opacity: 0 }} transition={{ duration: 0.22, ease: "easeOut" }} className="overflow-hidden" data-testid={`rs-item-details-${idx}`}>
                        <div className="pt-5 mt-5 border-t border-slate-100">
                          <p className="text-xs uppercase tracking-wider font-semibold text-slate-400 mb-2">What do you see? (tap all that apply)</p>
                          <div className="grid grid-cols-3 sm:grid-cols-5 gap-2 mb-4">
                            {CONDITION_TAGS.map((t) => {
                              const Icon = TAG_ICONS[t.icon] || Tag;
                              const on = (it.condition_tags || []).includes(t.value);
                              return (
                                <button key={t.value} onClick={() => toggleTag(idx, t.value)} data-testid={`rs-tag-${idx}-${t.value}`}
                                  className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border-2 px-2 py-3 transition-all ${on ? "bg-slate-900 border-slate-900 text-white" : "bg-slate-50 border-slate-200 text-slate-600 hover:border-slate-400"}`}>
                                  <Icon className={`w-6 h-6 ${on ? "text-orange-400" : "text-slate-500"}`} />
                                  <span className="text-xs font-semibold text-center leading-tight">{t.label}</span>
                                </button>
                              );
                            })}
                          </div>
                          <div className="flex items-center justify-between mb-2">
                            <p className="text-xs uppercase tracking-wider font-semibold text-slate-400">Photos of this item ({it.photos.length})</p>
                            <button onClick={() => setCameraItem(idx)} data-testid={`rs-photo-btn-${idx}`}
                              className="inline-flex items-center gap-1.5 bg-orange-500 hover:bg-orange-600 text-white font-semibold text-sm px-4 py-2 rounded-lg">
                              <Camera className="w-4 h-4" /> Capture
                            </button>
                          </div>
                          {it.photos.length > 0 ? (
                            <div className="grid grid-cols-4 sm:grid-cols-6 gap-2" data-testid={`rs-photo-grid-${idx}`}>
                              {it.photos.map((p) => (
                                <div key={p.id} className="relative aspect-square rounded-lg overflow-hidden bg-slate-100 group">
                                  <img src={`${API_BASE}/claims/files/${p.storage_path}`} alt={p.original_filename} className="w-full h-full object-cover" />
                                  <button onClick={() => removePhoto(idx, p.id)} data-testid={`rs-remove-photo-${p.id}`} className="absolute top-1 right-1 w-7 h-7 rounded-full bg-black/60 hover:bg-red-600 flex items-center justify-center text-white">
                                    <X className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              ))}
                            </div>
                          ) : (
                            <div className="flex items-center gap-2 text-sm rounded-lg px-4 py-4 justify-center border text-amber-700 bg-amber-50 border-amber-300 border-dashed font-semibold" data-testid={`rs-photo-required-${idx}`}>
                              <ImageOff className="w-4 h-4" /> Photo required — take at least one before completing.
                            </div>
                          )}
                        </div>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </div>
              ))}
              {items.length === 0 && (
                <div className="bg-white/5 border border-white/10 rounded-2xl p-6 text-center text-white/60">
                  No line items on this shipment. Add photos and a reason, then record the return.
                </div>
              )}
            </div>

            {/* Reason */}
            <div className="mt-8">
              <label className="block text-sm font-semibold mb-2">Return reason (optional)</label>
              <select value={reason} onChange={(e) => setReason(e.target.value)} data-testid="rs-reason"
                className="w-full sm:w-96 text-base px-4 py-3 rounded-xl bg-white text-slate-900 focus:outline-none focus:ring-4 focus:ring-orange-400/40">
                <option value="">Select a reason…</option>
                {RETURN_REASONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </div>
          </div>
        )}

        {/* STEP 4 — done */}
        {step === "done" && lastResult && (
          <div className="max-w-xl mx-auto px-6 py-16 text-center" data-testid="rs-step-done">
            <div className="w-24 h-24 mx-auto rounded-full bg-emerald-500/20 flex items-center justify-center mb-6">
              <Check className="w-14 h-14 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-extrabold mb-2 text-white" style={{ fontFamily: "Manrope" }}>Return recorded</h1>
            <p className="text-white/60 mb-8">
              Order <span className="font-mono font-bold text-white">{lastResult.order_number}</span> · {lastResult.units} unit{lastResult.units === 1 ? "" : "s"} · {lastResult.photos} photo{lastResult.photos === 1 ? "" : "s"}.
              The brand will disposition it from their dashboard, and it lands in tonight&apos;s 6 PM digest.
            </p>
            <div className="flex gap-3 justify-center">
              <button onClick={resetForNext} data-testid="rs-process-another" className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white font-bold text-lg px-8 py-5 rounded-2xl">
                <ScanLine className="w-6 h-6" /> Add another
              </button>
              <button onClick={() => navigate("/admin")} data-testid="rs-finish" className="inline-flex items-center gap-2 bg-white/10 hover:bg-white/20 text-white font-semibold px-6 py-5 rounded-2xl">
                <LogOut className="w-5 h-5" /> Exit returns
              </button>
            </div>
          </div>
        )}
      </main>

      {/* Sticky complete bar */}
      {step === "inspect" && (
        <div className="shrink-0 bg-slate-950/80 border-t border-white/10 px-6 py-4 flex items-center justify-between gap-4">
          <p className="text-sm text-white/60">
            {returnedItems} item{returnedItems === 1 ? "" : "s"} · {totalUnits} units · {items.reduce((s, i) => s + i.photos.length, 0)} photos
            {missingPhoto.length > 0 && <span className="ml-2 text-amber-400 font-semibold" data-testid="rs-photo-warning">· {missingPhoto.length} item{missingPhoto.length === 1 ? "" : "s"} need a photo</span>}
          </p>
          <button onClick={complete} disabled={saving || uploading || totalUnits === 0 || missingPhoto.length > 0} data-testid="rs-complete-btn"
            title={missingPhoto.length > 0 ? "Take at least one photo of every returned item first" : ""}
            className="inline-flex items-center gap-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-40 disabled:cursor-not-allowed text-white font-bold text-lg px-10 py-4 rounded-2xl">
            {saving ? <Loader2 className="w-6 h-6 animate-spin" /> : <PackageCheck className="w-6 h-6" />} Complete return
          </button>
        </div>
      )}

      {scanOrder && (
        <BarcodeScanner title={mode === "tracking" ? "Scan tracking barcode" : "Scan order barcode"}
          onDetected={(code) => { setScanOrder(false); doLookup(code); }}
          onClose={() => setScanOrder(false)} />
      )}
      {scanItems && (
        <BarcodeScanner title="Scan items to verify" continuous
          onDetected={onItemScan} onClose={() => setScanItems(false)} />
      )}
      {cameraItem != null && (
        <CameraCapture label={`Photo — ${items[cameraItem]?.name || "item"}`} busy={uploading}
          onCapture={(blob, name) => uploadPhoto(cameraItem, blob, name)}
          onClose={() => setCameraItem(null)} />
      )}
      {zoomImg && (
        <div className="fixed inset-0 z-[100] bg-black/90 flex flex-col items-center justify-center p-6" onClick={() => setZoomImg(null)} data-testid="rs-img-zoom">
          <button className="absolute top-4 right-4 text-white/80 hover:text-white p-2" onClick={() => setZoomImg(null)} data-testid="rs-img-zoom-close"><X className="w-8 h-8" /></button>
          <img src={zoomImg.url} alt={zoomImg.name} referrerPolicy="no-referrer" className="max-h-[82vh] max-w-[92vw] rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
          {zoomImg.name && <p className="text-white/80 text-sm mt-3 font-semibold">{zoomImg.name}</p>}
          <p className="text-white/40 text-xs mt-1">ShipStation reference photo · tap anywhere to close</p>
        </div>
      )}
    </div>
  );
}
